/**
 * SendJourneyAccessModal — "Manage access" for a Client Hub (2026-07 — per-recipient access,
 * database/134_journey_recipients.sql).
 *
 * Opens via CustomEvent('open-send-journey-access-modal', { detail: { journeyId, projectId } }).
 * No signup, no account — each recipient still just gets a bare link + 6-digit PIN, but now one
 * PER PERSON instead of one shared by the whole journey: a husband and wife each get their own,
 * individually revocable, individually attributable in Slack notifications and the e-signature
 * audit trail (see docs/CLIENT_JOURNEY.md's "Per-recipient access" section).
 *
 * Two parts:
 *   1. The current recipient list — resend (same link+PIN) / regenerate (rotate just THEIRS) /
 *      revoke (or reactivate), per row. A revoked row also offers "Remove from list" — permanent
 *      deletion, gated behind revoke-first so a live credential can't be deleted in one click
 *      (e.g. clearing out a recipient added by mistake, like a wrong email address).
 *   2. "+ Add recipients" — check people from the project's Client ORGANIZATION's Contacts
 *      (database/137_journey_recipients_contact_link.sql), or add a name+email manually for
 *      anyone not in Contacts yet. Deliberately NOT sourced from Project Teams (services/team.js's
 *      groupTeamRows) — that's a separate, later, manual step (added via TeamMemberPickerModal),
 *      with no connection to Opportunity conversion at all, so it's typically still empty at the
 *      Opportunity/prospect stage even though the Client org + Contact records already exist by
 *      then (created by the Convert Lead Zap). Organization.Contacts is available immediately and
 *      remains a superset once Team does get built out. A contact picked this way is stored as
 *      `contact_airtable_id` on the recipient row — the fix for "orphan" recipients with no
 *      relationship to the real Contact record for that same person. Staged entries send as one
 *      batch.
 *
 * A journey's original shared link+PIN (database/133_client_journey_access.sql) still works for
 * anyone who already has it — nothing here breaks that — but it's never issued to anyone new. If
 * one is still live, a small "Legacy shared link" row offers revoking it (rotating it away so
 * nobody currently holding it can use it anymore, without sending the new value to anyone) — this
 * check also requires `!journey.legacy_link_revoked` (database/136), since rotating the credential
 * still leaves *a* pin in place and checking `journey.pin` alone kept showing this row forever
 * after revoking it once.
 */

import { useState, useEffect, useCallback } from 'react'
import {
  listRecipients, addRecipients, revokeRecipient, reactivateRecipient, deleteRecipient,
  resendRecipientAccess, regenerateRecipientAccess, getLink, regenerateToken, setRecipientRole,
} from '../services/journey.js'
import { getProjectClientOrgId } from '../services/team.js'
import { syncOpportunityContactsToClient } from '../services/opportunities.js'
import { getOrganizationDetail, getContactNamesByIds, getContactEmailsByIds } from '../services/contacts.js'
import { ActionMenu, ActionMenuButton, ActionMenuItem, ActionMenuDivider, useActionMenu } from './ActionMenu.jsx'
import { confirmDialog } from './Dialog.jsx'

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const labelStyle = {
  display: 'block', fontFamily: '"DM Mono",monospace', fontSize: 10,
  color: 'var(--aj-text-mid)', letterSpacing: '0.04em', marginBottom: 4, textTransform: 'uppercase',
}
const inputStyle = {
  width: '100%', padding: '7px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 12,
  color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)',
  borderRadius: 4, boxSizing: 'border-box', outline: 'none',
}
const smallBtn = { padding: '5px 10px', fontFamily: '"DM Sans",sans-serif', fontSize: 11, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)', whiteSpace: 'nowrap' }

function fmtWhen(iso) {
  if (!iso) return 'Never opened'
  const d = new Date(iso)
  return 'Opened ' + d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

export default function SendJourneyAccessModal() {
  const [open, setOpen] = useState(false)
  const [journeyId, setJourneyId] = useState(null)
  const [projectId, setProjectId] = useState(null)

  const [recipients, setRecipients] = useState(null)   // null = loading
  const [legacyPin, setLegacyPin] = useState(null)      // journey.pin, if a shared credential still exists

  const [clientContacts, setClientContacts] = useState(null)   // project's Client-team contacts with an email on file
  const [checked, setChecked] = useState({})                    // contactId -> bool
  const [staged, setStaged] = useState([])                      // manual [{name,email}]
  const [manualName, setManualName] = useState('')
  const [manualEmail, setManualEmail] = useState('')
  const [manualRep, setManualRep] = useState(false)   // owner's rep: no money, no signing (docs/CLIENT_HUB_OWNERS_REP.md)

  const [sending, setSending] = useState(false)
  const [error, setError] = useState('')

  const reload = useCallback(() => {
    if (!journeyId) return
    listRecipients(journeyId).then(setRecipients).catch(() => setRecipients([]))
  }, [journeyId])

  useEffect(() => {
    function onOpen(e) {
      const d = e.detail || {}
      if (!d.journeyId) return
      setJourneyId(d.journeyId); setProjectId(d.projectId || null)
      setRecipients(null); setLegacyPin(null)
      setClientContacts(null); setChecked({}); setStaged([]); setManualName(''); setManualEmail(''); setManualRep(false)
      setError(''); setSending(false)
      setOpen(true)
    }
    window.addEventListener('open-send-journey-access-modal', onOpen)
    return () => window.removeEventListener('open-send-journey-access-modal', onOpen)
  }, [])

  useEffect(() => { if (open) reload() }, [open, reload])

  // Legacy shared-link check — only relevant if the journey still carries a PIN on its own row
  // AND that credential hasn't already been intentionally revoked (legacy_link_revoked,
  // database/136 — rotating it away via regenerateToken still leaves *a* pin in place, just a new
  // one nobody has, so checking journey.pin alone kept showing this row forever after revoking).
  useEffect(() => {
    if (!open || !projectId) return
    getLink(projectId).then(({ journey }) => setLegacyPin(journey && journey.pin && !journey.legacy_link_revoked ? journey.pin : null)).catch(() => setLegacyPin(null))
  }, [open, projectId])

  // The project's Client ORGANIZATION's Contacts (database/137) — NOT Project Teams, which is a
  // separate later step with no connection to conversion (see the file header comment). Available
  // the moment a Client org is linked, which happens at Opportunity creation itself.
  useEffect(() => {
    if (!open || !projectId) return
    setClientContacts(null)
    // The opportunity's contacts join its client organization first, so the person the deal is
    // with is always offered here (best-effort: a failure still shows the organization as it is).
    syncOpportunityContactsToClient(projectId).catch(() => false)
      .then(() => getProjectClientOrgId(projectId))
      .then(async clientOrgId => {
        if (!clientOrgId) { setClientContacts([]); return }
        const org = await getOrganizationDetail(clientOrgId)
        const contactIds = (org && org.fields && org.fields['Contacts']) || []
        if (!contactIds.length) { setClientContacts([]); return }
        const [names, emails] = await Promise.all([getContactNamesByIds(contactIds), getContactEmailsByIds(contactIds)])
        setClientContacts(contactIds.map(id => ({ contactId: id, name: names[id] || 'Unknown', email: emails[id] || '' })).filter(c => c.email))
      })
      .catch(() => setClientContacts([]))
  }, [open, projectId])

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape' && open) setOpen(false) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  if (!open) return null

  function addManual() {
    const name = manualName.trim(), email = manualEmail.trim()
    if (!name || !EMAIL_RE.test(email)) return
    setStaged(s => s.concat([{ name, email, role: manualRep ? 'owners_rep' : 'client' }]))
    setManualName(''); setManualEmail(''); setManualRep(false)
  }

  const activeEmails = new Set((recipients || []).filter(r => r.status === 'active').map(r => r.email.toLowerCase()))
  const toSend = [
    ...Object.keys(checked).filter(id => checked[id]).map(id => {
      const c = (clientContacts || []).find(x => x.contactId === id)
      return c ? { name: c.name, email: c.email, contactId: c.contactId } : null
    }).filter(Boolean),
    ...staged,
  ].filter(r => !activeEmails.has(r.email.toLowerCase()))

  async function handleSend() {
    if (!toSend.length || sending) return
    setSending(true); setError('')
    try {
      await addRecipients(journeyId, toSend)
      setChecked({}); setStaged([])
      reload()
    } catch (ex) {
      setError(ex.message || 'Failed to send')
    } finally {
      setSending(false)
    }
  }

  async function handleRevoke(r) {
    if (!(await confirmDialog({ title: 'Revoke access?', message: `${r.name}'s link and access code stop working immediately. This doesn't affect anyone else on this Client Hub.`, confirmLabel: 'Revoke', danger: true }))) return
    await revokeRecipient(r.id); reload()
  }
  async function handleReactivate(r) { await reactivateRecipient(r.id); reload() }
  async function handleRole(r) {
    const toRep = r.role !== 'owners_rep'
    const ok = await confirmDialog(toRep
      ? { title: "Make an owner's rep?", message: `${r.name} keeps their link and sees the project's updates, but no invoices, no proposals or additional services, and nothing to sign. They can't approve or answer for the owner. Comments on inspiration pictures, the punch list and the room program still work.`, confirmLabel: "Make owner's rep" }
      : { title: 'Make a client?', message: `${r.name} will see everything the owner sees, including invoices and proposals, and can approve and sign.`, confirmLabel: 'Make client' })
    if (!ok) return
    try { await setRecipientRole(r.id, toRep ? 'owners_rep' : 'client'); reload() } catch (ex) { setError(ex.message || 'Failed to change') }
  }
  async function handleDelete(r) {
    if (!(await confirmDialog({ title: 'Remove from list?', message: `Permanently removes ${r.name} (${r.email}) from this Client Hub's recipient list — e.g. if they were added by mistake. Their past responses, if any, are kept but no longer linked to a name. This can't be undone.`, confirmLabel: 'Remove', danger: true }))) return
    try { await deleteRecipient(r.id); reload() } catch (ex) { setError(ex.message || 'Failed to remove') }
  }
  async function handleResend(r) {
    try { await resendRecipientAccess(r.id) } catch (ex) { setError(ex.message || 'Failed to send') }
  }
  async function handleRegenerate(r) {
    if (!(await confirmDialog({ title: 'Regenerate their access?', message: `${r.name}'s CURRENT link and access code stop working immediately and a new one is emailed to them. Use this if they lost access, or if you suspect their link was shared with someone else.`, confirmLabel: 'Regenerate & resend' }))) return
    try { await regenerateRecipientAccess(r.id); reload() } catch (ex) { setError(ex.message || 'Failed to regenerate') }
  }
  async function handleRevokeLegacy() {
    if (!(await confirmDialog({ title: 'Revoke the legacy shared link?', message: 'Anyone still using the original shared link+code (issued before per-person access existed) loses access immediately. This never affects any individual recipient above.', confirmLabel: 'Revoke', danger: true }))) return
    try { await regenerateToken(journeyId); setLegacyPin(null) } catch (ex) { setError(ex.message || 'Failed to revoke') }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 600, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--aj-bg-elevated)', border: '0.5px solid var(--aj-border)', borderRadius: 8, boxShadow: '0 12px 36px rgba(0,0,0,0.16)', width: 560, maxWidth: '94vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '0.5px solid var(--aj-border)', flexShrink: 0 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 14, fontWeight: 700, color: 'var(--aj-text)' }}>Manage Client Hub access</span>
          <button onClick={() => setOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 18, lineHeight: 1, padding: 2 }}>×</button>
        </div>

        <div style={{ padding: '16px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 16 }}>
          {/* Current recipients */}
          <div>
            <label style={labelStyle}>Recipients</label>
            {recipients === null && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' }}>Loading…</div>}
            {recipients && recipients.length === 0 && (
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)', fontStyle: 'italic' }}>
                No one has been sent access yet — add recipients below.
              </div>
            )}
            {recipients && recipients.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', border: '0.5px solid var(--aj-border)', borderRadius: 6, overflow: 'hidden' }}>
                {recipients.map(r => (
                  <RecipientRow key={r.id} r={r}
                    onResend={() => handleResend(r)} onRegenerate={() => handleRegenerate(r)}
                    onRevoke={() => handleRevoke(r)} onReactivate={() => handleReactivate(r)}
                    onDelete={() => handleDelete(r)} onRole={() => handleRole(r)} />
                ))}
              </div>
            )}
          </div>

          {legacyPin && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '9px 12px', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border)', borderRadius: 6 }}>
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', lineHeight: 1.4 }}>
                A legacy shared link (issued before per-person access) is still live.
              </div>
              <button onClick={handleRevokeLegacy} style={{ ...smallBtn, color: '#c0392b', borderColor: 'rgba(192,57,43,0.35)' }}>Revoke</button>
            </div>
          )}

          <hr style={{ border: 'none', borderTop: '0.5px solid var(--aj-border)', margin: 0 }} />

          {/* Add recipients */}
          <div>
            <label style={labelStyle}>Add recipients</label>
            {clientContacts === null && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-muted)' }}>Loading the client's contacts…</div>}
            {clientContacts && clientContacts.length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 10 }}>
                {clientContacts.map(c => {
                  const already = activeEmails.has(c.email.toLowerCase())
                  return (
                    <label key={c.contactId} style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, color: already ? 'var(--aj-text-muted)' : 'var(--aj-text)', cursor: already ? 'default' : 'pointer' }}>
                      <input type="checkbox" disabled={already} checked={!!checked[c.contactId]} onChange={e => setChecked(prev => ({ ...prev, [c.contactId]: e.target.checked }))} />
                      {c.name} <span style={{ color: 'var(--aj-text-muted)', fontSize: 11 }}>{c.email}</span>
                      {already && <span style={{ fontSize: 10, color: '#1D9E75' }}>· already has access</span>}
                    </label>
                  )
                })}
              </div>
            )}

            <div style={{ display: 'flex', gap: 6, marginBottom: staged.length ? 8 : 0 }}>
              <input style={{ ...inputStyle, flex: '0 1 150px' }} value={manualName} onChange={e => setManualName(e.target.value)} placeholder="Name" />
              <input type="email" style={{ ...inputStyle, flex: 1 }} value={manualEmail} onChange={e => setManualEmail(e.target.value)} placeholder="email@example.com"
                onKeyDown={e => { if (e.key === 'Enter') addManual() }} />
              <button onClick={addManual} disabled={!manualName.trim() || !EMAIL_RE.test(manualEmail.trim())} style={smallBtn}>+ Add</button>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: staged.length ? 8 : 0, fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text-mid)', cursor: 'pointer' }}>
              <input type="checkbox" checked={manualRep} onChange={e => setManualRep(e.target.checked)} />
              Owner's rep: sees the updates, no invoices, proposals or signing
            </label>

            {staged.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {staged.map((s, i) => (
                  <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 8px', background: 'var(--aj-bg-subtle)', border: '0.5px solid var(--aj-border-mid)', borderRadius: 20, fontFamily: '"DM Sans",sans-serif', fontSize: 11.5, color: 'var(--aj-text)' }}>
                    {s.name} <span style={{ color: 'var(--aj-text-muted)' }}>{s.email}</span>
                    {s.role === 'owners_rep' && <span style={{ fontSize: 10, color: 'var(--aj-text-mid)' }}>owner's rep</span>}
                    <button onClick={() => setStaged(list => list.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', fontSize: 13, lineHeight: 1, padding: 0 }}>×</button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: '#c0392b' }}>{error}</div>}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '14px 20px', borderTop: '0.5px solid var(--aj-border)', flexShrink: 0 }}>
          <button onClick={() => setOpen(false)} style={{ padding: '7px 16px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, background: 'none', border: '0.5px solid var(--aj-border-mid)', borderRadius: 4, cursor: 'pointer', color: 'var(--aj-text)' }}>
            Close
          </button>
          <button onClick={handleSend} disabled={!toSend.length || sending} style={{ padding: '7px 18px', fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, background: (toSend.length && !sending) ? '#07272D' : 'var(--aj-bg-muted)', color: (toSend.length && !sending) ? '#fff' : 'var(--aj-text-muted)', border: 'none', borderRadius: 4, cursor: (toSend.length && !sending) ? 'pointer' : 'default' }}>
            {sending ? 'Sending…' : `Send access${toSend.length ? ' (' + toSend.length + ')' : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}

function RecipientRow({ r, onResend, onRegenerate, onRevoke, onReactivate, onDelete, onRole }) {
  const menu = useActionMenu()
  const revoked = r.status === 'revoked'
  return (
    <div className="aj-recip-row" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderBottom: '0.5px solid var(--aj-border)' }}>
      {/* ActionMenuButton reuses the outline's .wf-menu-btn styling, which starts at opacity:0 and
          only reveals on `.wf-row:hover` — the outline's own row class, which this modal's plain
          flex rows don't carry. Left as-is, the ⋯ trigger is invisible (though still clickable)
          everywhere outside the outline itself. Scoped override: dim-but-visible by default,
          full opacity on hover — same reveal *feel*, without depending on .wf-row. */}
      <style>{`
        .aj-recip-row .wf-menu-btn { opacity: 0.4; }
        .aj-recip-row:hover .wf-menu-btn { opacity: 1; }
      `}</style>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12.5, fontWeight: 600, color: 'var(--aj-text)' }}>{r.name}</span>
          {r.role === 'owners_rep' && <span className="wf-badge wf-badge--gray">Owner's rep</span>}
          {revoked && <span className="wf-badge wf-badge--gray">Revoked</span>}
        </div>
        <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 10.5, color: 'var(--aj-text-muted)', marginTop: 1 }}>
          {r.email} · {fmtWhen(r.last_opened_at)}
        </div>
      </div>
      <ActionMenuButton menu={menu} />
      <ActionMenu menu={menu}>
        {!revoked && <ActionMenuItem onClick={onResend}>Resend access</ActionMenuItem>}
        {!revoked && <ActionMenuItem onClick={onRegenerate}>Regenerate & resend</ActionMenuItem>}
        {!revoked && <ActionMenuItem onClick={onRole}>{r.role === 'owners_rep' ? 'Make a client' : "Make an owner's rep"}</ActionMenuItem>}
        <ActionMenuDivider />
        {revoked
          ? <>
              <ActionMenuItem onClick={onReactivate}>Reactivate</ActionMenuItem>
              <ActionMenuItem onClick={onDelete} danger>Remove from list</ActionMenuItem>
            </>
          : <ActionMenuItem onClick={onRevoke} danger>Revoke</ActionMenuItem>}
      </ActionMenu>
    </div>
  )
}
